import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, RefreshCw, Inbox, Lock, LogOut, Loader2, Mail, Send, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "oris_admin_token";

export default function Admin() {
  const { t } = useLang();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySending, setReplySending] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setMessages([]);
    setReplyFor(null);
  }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        toast.error(t.admin.sessionExpired);
      } else {
        setMessages([]);
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, t]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
    } catch (err) {
      setError(t.admin.loginError);
    } finally {
      setLoading(false);
    }
  };

  const setStatus = async (m, status) => {
    try {
      const res = await axios.patch(
        `${API}/contact/${m.id}/status`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages((prev) => prev.map((x) => (x.id === m.id ? res.data : x)));
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        toast.error(t.admin.sessionExpired);
      } else {
        toast.error(t.admin.replyError);
      }
    }
  };

  const sendReply = async (m) => {
    setReplySending(true);
    try {
      await axios.post(
        `${API}/admin/reply`,
        { message_id: m.id, reply: replyText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t.admin.replySuccess);
      setReplyFor(null);
      setReplyText("");
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        toast.error(t.admin.sessionExpired);
      } else {
        toast.error(t.admin.replyError);
      }
    } finally {
      setReplySending(false);
    }
  };

  if (!token) {
    return (
      <div data-testid="admin-login-page" className="grain relative flex min-h-screen items-center justify-center bg-oris-green px-5">
        <div className="w-full max-w-md border border-oris-cream/15 bg-white p-8 shadow-[0_24px_80px_rgb(0,0,0,0.25)] sm:p-10">
          <div className="flex items-center justify-between">
            <Logo tone="dark" compact />
            <span className="flex h-10 w-10 items-center justify-center bg-oris-green text-oris-gold">
              <Lock className="h-5 w-5" />
            </span>
          </div>
          <h1 data-testid="admin-login-title" className="mt-8 font-serif text-3xl font-medium text-oris-ink">
            {t.admin.loginTitle}
          </h1>
          <p className="mt-2 text-sm text-oris-moss">{t.admin.loginIntro}</p>

          <form onSubmit={login} className="mt-8">
            <label htmlFor="admin-password" className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">
              {t.admin.passwordLabel}
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-oris-green/20 bg-oris-cream px-4 py-3.5 text-sm text-oris-ink transition-[border-color] duration-300 focus:border-oris-gold"
            />
            {error && (
              <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-red-700">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-button"
              type="submit"
              disabled={loading}
              className="mt-6 inline-flex w-full items-center justify-center gap-2 bg-oris-green px-6 py-4 text-[0.8rem] font-bold uppercase tracking-[0.16em] text-oris-cream transition-colors duration-300 hover:bg-oris-deep disabled:opacity-60"
            >
              {loading ? t.admin.loggingIn : t.admin.loginButton}
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            </button>
          </form>

          <Link
            to="/"
            data-testid="admin-login-back-link"
            className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-oris-moss transition-colors hover:text-oris-gold"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t.admin.back}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-oris-cream">
      <div className="border-b border-oris-green/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
          <Logo tone="dark" compact />
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-refresh-button"
              onClick={load}
              className="inline-flex items-center gap-2 border border-oris-green/25 px-4 py-2 text-xs font-bold uppercase tracking-widest text-oris-green transition-colors hover:border-oris-gold hover:text-oris-gold"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              {t.admin.refresh}
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-oris-green/25 px-4 py-2 text-xs font-bold uppercase tracking-widest text-oris-green transition-colors hover:border-red-700 hover:text-red-700"
            >
              <LogOut className="h-3.5 w-3.5" />
              {t.admin.logout}
            </button>
            <Link
              to="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-2 bg-oris-green px-4 py-2 text-xs font-bold uppercase tracking-widest text-oris-cream transition-colors hover:bg-oris-deep"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              {t.admin.back}
            </Link>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <h1 data-testid="admin-title" className="font-serif text-4xl font-medium text-oris-ink">
          {t.admin.title}
          <span className="ml-3 align-middle font-sans text-xs font-bold uppercase tracking-widest text-oris-gold">
            {messages.length} {t.admin.count}
          </span>
        </h1>

        {!loading && messages.length === 0 && (
          <div data-testid="admin-empty-state" className="mt-12 flex flex-col items-center gap-4 border border-dashed border-oris-green/25 py-20 text-oris-moss">
            <Inbox className="h-10 w-10" strokeWidth={1.4} />
            <p className="text-sm">{t.admin.empty}</p>
          </div>
        )}

        <div data-testid="admin-messages-list" className="mt-10 space-y-4">
          {messages.map((m) => (
            <article key={m.id} data-testid={`admin-message-${m.id}`} className="border border-oris-green/12 bg-white p-6">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-serif text-2xl text-oris-ink">{m.name}</h2>
                <time className="text-xs uppercase tracking-widest text-oris-moss/70">
                  {new Date(m.created_at).toLocaleString()}
                </time>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-6 gap-y-1 text-sm text-oris-moss">
                <span>{m.email}</span>
                {m.company && <span>{m.company}</span>}
                {m.subject && <span className="font-semibold text-oris-gold">{m.subject}</span>}
              </div>
              <p className="mt-4 border-t border-oris-green/10 pt-4 text-sm leading-relaxed text-oris-ink/80">{m.message}</p>

              <div data-testid={`admin-status-row-${m.id}`} className="mt-5 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-[0.62rem] font-bold uppercase tracking-[0.18em] text-oris-moss">{t.admin.statusLabel}</span>
                {["nouveau", "en_cours", "traite"].map((s) => (
                  <button
                    key={s}
                    data-testid={`admin-status-${s}-${m.id}`}
                    onClick={() => setStatus(m, s)}
                    className={`border px-3 py-1.5 text-[0.68rem] font-bold uppercase tracking-widest transition-colors duration-300 ${
                      (m.status || "nouveau") === s
                        ? "border-oris-green bg-oris-green text-oris-cream"
                        : "border-oris-green/25 text-oris-moss hover:border-oris-gold hover:text-oris-gold"
                    }`}
                  >
                    {t.admin.status[s]}
                  </button>
                ))}
              </div>

              {replyFor === m.id ? (
                <div data-testid={`admin-reply-form-${m.id}`} className="mt-5 border border-oris-green/15 bg-oris-cream p-5">
                  <label htmlFor={`reply-${m.id}`} className="mb-2 block text-[0.68rem] font-bold uppercase tracking-[0.2em] text-oris-moss">
                    {t.admin.replyLabel}
                  </label>
                  <textarea
                    id={`reply-${m.id}`}
                    data-testid={`admin-reply-input-${m.id}`}
                    rows={4}
                    autoFocus
                    placeholder={t.admin.replyPh}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    className="w-full resize-y border border-oris-green/20 bg-white px-4 py-3 text-sm text-oris-ink transition-[border-color] duration-300 focus:border-oris-gold"
                  />
                  <div className="mt-4 flex gap-3">
                    <button
                      data-testid={`admin-reply-send-${m.id}`}
                      onClick={() => sendReply(m)}
                      disabled={replySending || replyText.trim().length < 2}
                      className="inline-flex items-center gap-2 bg-oris-green px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-oris-cream transition-colors hover:bg-oris-deep disabled:opacity-60"
                    >
                      {replySending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                      {replySending ? t.admin.replySending : t.admin.replySend}
                    </button>
                    <button
                      data-testid={`admin-reply-cancel-${m.id}`}
                      onClick={() => { setReplyFor(null); setReplyText(""); }}
                      className="inline-flex items-center gap-2 border border-oris-green/25 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-oris-green transition-colors hover:border-red-700 hover:text-red-700"
                    >
                      <X className="h-3.5 w-3.5" />
                      {t.admin.replyCancel}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  data-testid={`admin-reply-open-${m.id}`}
                  onClick={() => { setReplyFor(m.id); setReplyText(""); }}
                  className="mt-5 inline-flex items-center gap-2 border border-oris-green/25 px-4 py-2 text-xs font-bold uppercase tracking-widest text-oris-green transition-colors hover:border-oris-gold hover:text-oris-gold"
                >
                  <Mail className="h-3.5 w-3.5" />
                  {t.admin.reply}
                </button>
              )}
            </article>
          ))}
        </div>
      </main>
    </div>
  );
}
